import { AnimatePresence, motion } from 'motion/react';
import type { ReactNode } from 'react';
import { useEffect, useId, useRef } from 'react';
import s from './ui.module.css';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  title,
  children,
  width,
  dismissable = true,
  testId,
}: {
  open: boolean;
  onClose?: () => void;
  title?: ReactNode;
  children: ReactNode;
  width?: number;
  dismissable?: boolean;
  testId?: string;
}) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  // Foco dentro del diálogo mientras está abierto (y de vuelta al cerrar).
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const t = window.setTimeout(() => {
      const el = dialogRef.current;
      const first = el?.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? el)?.focus();
    }, 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissable) onClose?.();
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const items = [...dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, [open, dismissable, onClose]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className={s.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={(e) => {
            if (dismissable && e.target === e.currentTarget) onClose?.();
          }}
        >
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            tabIndex={-1}
            data-testid={testId}
            className={[s.panel, s.modal].join(' ')}
            style={width ? { ['--modal-w' as string]: `${width}px` } : undefined}
            initial={{ opacity: 0, y: 18, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
          >
            {title ? (
              <h2 id={titleId} className={s.modalTitle}>
                {title}
              </h2>
            ) : null}
            {dismissable && onClose ? (
              <button type="button" className={[s.btn, s.ghost, s.small, s.modalClose].join(' ')} onClick={onClose} aria-label="Cerrar">
                ✕
              </button>
            ) : null}
            {children}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export function Panel({
  children,
  className,
  style,
  testId,
  dataCard,
}: {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  testId?: string;
  dataCard?: string;
}) {
  return (
    <div className={[s.panel, className ?? ''].join(' ')} style={style} data-testid={testId} data-card={dataCard}>
      {children}
    </div>
  );
}
